/**
 * BootstrapErrorScreen
 *
 * Full-screen surface shown when the app bootstrap fails with a transient error
 * (network / 503) and the authentication state is unknown. Renders the branded
 * dual-column layout — a "lost route" map illustration on the left and an
 * explanatory message plus a retry action on the right — matching the public
 * share status / password gate and the onboarding / invitation screens.
 *
 * @module components/common/BootstrapErrorScreen
 */
import { memo } from 'react';
import { Button, Stack, Text } from '@mantine/core';
import { IconPlugConnectedX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { SplitScreenLayout } from '@/components/layout/SplitScreenLayout';
import { GateMobileLayout } from '@/components/layout/GateMobileLayout';
import { OnboardingSettingsBar } from '@/components/layout/OnboardingSettingsBar';
import { SplitScreenBrandPanel } from '@/components/common/SplitScreenBrandPanel';
import { RouteErrorIllustration } from '@/components/common/illustrations/RouteErrorIllustration';
import { useDeviceLayout } from '@/hooks';
import { ICON_SIZE_EMPTY_STATE } from '@/constants/ui';

import styles from './BootstrapErrorScreen.module.css';

interface BootstrapErrorScreenProps {
  onRetry: () => void;
}

export const BootstrapErrorScreen = memo(function BootstrapErrorScreen({
  onRetry,
}: BootstrapErrorScreenProps) {
  const { t } = useTranslation();
  const { isMobile } = useDeviceLayout();

  const messageContent = (
    <div className={styles.rightMessageWrapper}>
      <Stack gap="xs">
        <IconPlugConnectedX
          size={ICON_SIZE_EMPTY_STATE}
          className={styles.statusIcon}
          aria-hidden
        />
        <h2 className={styles.statusTitle}>
          {t('errors.bootstrapFailedTitle')}
        </h2>
        <Text c="dimmed">{t('errors.bootstrapFailedDescription')}</Text>
        <div className={styles.actions}>
          <Button onClick={onRetry}>{t('common.retry')}</Button>
        </div>
      </Stack>
    </div>
  );

  if (isMobile) {
    return <GateMobileLayout>{messageContent}</GateMobileLayout>;
  }

  return (
    <SplitScreenLayout
      leftPanelBackground={<RouteErrorIllustration />}
      leftPanel={
        <SplitScreenBrandPanel
          heading={t('errors.bootstrapLeftHeading')}
          description={t('errors.bootstrapLeftDescription')}
          footer={t('errors.bootstrapLeftFooter')}
        />
      }
      settingsBar={<OnboardingSettingsBar />}
      rightPanel={messageContent}
    />
  );
});
